import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Wand2, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { canWrite } from '../lib/roles';
import { cn } from '../lib/cn';
import { MORPH } from '../lib/motion';
import { useCompChecklist } from '../lib/useCompChecklist';
import { useOrgChecklist } from '../lib/useOrgChecklist';
import { encodeSetupJump, type JumpableItem } from '../lib/setupJump';

export type GuideTrack = 'comp' | 'org';

// ─────────────────────────────────────────────────────────────────────────────
// GuideMeLauncher — Phase 4a repurposing (modal retired). Used to open a
// modal wizard overlay (WizardShell + CompetitionWizard/OrgSetupWizard — all
// deleted, see TournamentSetup's header comment); now it's a plain
// "Guide me → {next step}" nudge pointing at the first unfinished checklist
// item, computed from the SAME useCompChecklist/useOrgChecklist hooks the
// on-page SetupProgressStrip renders from — the label here and the strip's
// own ✓/○ row can never disagree, because they share one cache.
//
// Two ways a click can act:
//   - `onJump` given (TournamentSetup passes its own `performJump`) — call it
//     directly. Instant, no navigation, no reload — this is the mount inside
//     Setup itself.
//   - `onJump` absent (e.g. mounted on Organizations.tsx, which no longer
//     hosts any org identity/branding/Discord controls itself — they all
//     live on Setup's Organisation tab now) — navigate to
//     `/admin/setup?jump=<tab>:<sectionId>`; TournamentSetup decodes that
//     param once on mount (lib/setupJump.ts) and performs the same jump.
// An item with `externalHref` (org branding/veto — configured on a separate
// route, not inline on Setup) always navigates there directly either way.
// ─────────────────────────────────────────────────────────────────────────────

export function GuideMeLauncher({
  orgId,
  compId,
  track,
  onJump,
}: {
  orgId?: string;
  compId?: string;
  track: GuideTrack;
  /** Direct jump — pass when mounted inside TournamentSetup itself. */
  onJump?: (item: JumpableItem) => void;
}) {
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });

  const orgChecklist = useOrgChecklist(orgId, canWrite(me?.role));
  const compChecklist = useCompChecklist(orgId, compId);
  const next = (track === 'org' ? orgChecklist.items : compChecklist.items).find(i => !i.done);

  const go = () => {
    if (!next) return;
    if (onJump) { onJump(next); return; }
    if (next.externalHref) { navigate(next.externalHref); return; }
    navigate(`/admin/setup?jump=${encodeURIComponent(encodeSetupJump(next))}`);
  };

  if (!next) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] px-3.5 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-ink-meta">
        <CheckCircle2 className="h-3.5 w-3.5 text-accent-500" strokeWidth={2.25} />
        {track === 'org' ? 'Org ready' : 'Ready to activate'}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={go}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3.5 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-ink-secondary transition hover:bg-white/[0.1] hover:text-ink-primary',
        MORPH,
      )}
    >
      <Wand2 className="h-3.5 w-3.5 text-accent-500" strokeWidth={2.25} />
      Guide me → {next.label}
    </button>
  );
}
